import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import {
  Crown, Zap, Shield, ArrowLeft, Loader, Copy, Check,
  CreditCard, Calendar, Users, Gift, Share2, Link
} from 'lucide-react';
import { toast, Toaster } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const TIER_CONFIG = {
  free: { label: 'Free', color: '#5C5C5C', icon: Shield },
  pro: { label: 'Pro', color: '#007AFF', icon: Zap },
  business: { label: 'Business', color: '#FF3B30', icon: Users },
  founding: { label: 'Founding Member', color: '#FFD60A', icon: Crown }
};

const SubscriptionPage = () => {
  const { user, checkAuth } = useAuth();
  const navigate = useNavigate();
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cancelling, setCancelling] = useState(false);
  const [referral, setReferral] = useState(null);
  const [copied, setCopied] = useState(false);
  const [generatingCode, setGeneratingCode] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [subRes, refRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/subscription`, { withCredentials: true }),
        axios.get(`${BACKEND_URL}/api/referral/stats`, { withCredentials: true }).catch(() => ({ data: null }))
      ]);
      setSubscription(subRes.data);
      setReferral(refRes.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = async () => {
    if (!window.confirm('Are you sure you want to cancel? You will retain access until the end of your billing period.')) return;
    setCancelling(true);
    try {
      await axios.post(`${BACKEND_URL}/api/subscription/cancel`, {}, { withCredentials: true });
      toast.success('Subscription cancelled');
      await checkAuth();
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to cancel');
    } finally {
      setCancelling(false);
    }
  };

  const handleGenerateReferral = async () => {
    setGeneratingCode(true);
    try {
      const { data } = await axios.post(`${BACKEND_URL}/api/referral/generate`, {}, { withCredentials: true });
      setReferral({ code: data.code, uses: data.uses, referrals: [] });
      toast.success('Referral code generated!');
    } catch (err) {
      toast.error('Failed to generate code');
    } finally {
      setGeneratingCode(false);
    }
  };

  const copyReferralLink = () => {
    const link = `${window.location.origin}/register?ref=${referral?.code}`;
    navigator.clipboard.writeText(link);
    setCopied(true);
    toast.success('Referral link copied!');
    setTimeout(() => setCopied(false), 2000);
  };

  const copyCode = () => {
    navigator.clipboard.writeText(referral?.code);
    setCopied(true);
    toast.success('Code copied!');
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--main-bg)] flex items-center justify-center">
        <Loader size={32} className="animate-spin" />
      </div>
    );
  }

  const tier = subscription?.tier || 'free';
  const config = TIER_CONFIG[tier] || TIER_CONFIG.free;
  const TierIcon = config.icon;
  const isAdmin = subscription?.is_admin;
  const isPaid = tier !== 'free';

  return (
    <div className="min-h-screen bg-[var(--main-bg)] p-4 sm:p-8" data-testid="subscription-page">
      <Toaster position="top-right" />
      <div className="max-w-3xl mx-auto">
        <button
          onClick={() => navigate('/dashboard')}          className="flex items-center gap-2 text-[var(--text-secondary)] hover:text-[var(--obsidian)] mb-6 transition-colors"
          data-testid="back-to-dashboard"
        >
          <ArrowLeft size={20} />
          <span className="text-sm font-medium">Back to Dashboard</span>
        </button>

        <h1 className="text-3xl sm:text-5xl font-black tracking-tight mb-8" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
          Subscription
        </h1>

        {/* Current Plan Card */}
        <div className="bg-[var(--surface)] border-2 border-[var(--border-default)] p-8 mb-6" data-testid="current-plan-card">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-12 h-12 flex items-center justify-center border-2 border-[var(--border-default)]" style={{ backgroundColor: config.color + '20' }}>
              <TierIcon size={24} style={{ color: config.color }} />
            </div>
            <div>
              <div className="text-sm text-[var(--text-secondary)] font-medium">Current Plan</div>
              <div className="text-2xl font-black" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                {config.label}
                {isAdmin && <span className="ml-2 text-sm font-medium text-[var(--text-secondary)]">(Admin Access)</span>}
              </div>
            </div>
          </div>

          {subscription?.status && (
            <div className="flex items-center gap-4 text-sm text-[var(--text-secondary)] mt-4 flex-wrap">
              {subscription.status === 'active' && (
                <span className="flex items-center gap-1.5">
                  <div className="w-2 h-2 bg-green-500 rounded-full"></div> Active
                </span>
              )}
              {subscription.status === 'cancelled' && (
                <span className="flex items-center gap-1.5">
                  <div className="w-2 h-2 bg-yellow-500 rounded-full"></div> Cancelled (access until period end)
                </span>
              )}
              {subscription.started_at && (
                <span className="flex items-center gap-1.5">
                  <Calendar size={14} /> Since {new Date(subscription.started_at).toLocaleDateString()}
                </span>
              )}
              {subscription.expires_at && (
                <span className="flex items-center gap-1.5">
                  <CreditCard size={14} /> Renews {new Date(subscription.expires_at).toLocaleDateString()}
                </span>
              )}
            </div>
          )}

          <div className="flex flex-wrap gap-3 mt-6">
            {!isPaid && !isAdmin && (
              <button
                onClick={() => navigate('/pricing')}
                data-testid="upgrade-btn"
                className="bg-[var(--obsidian)] text-white font-bold px-6 py-3 border-2 border-[var(--border-default)] hover:translate-x-[2px] hover:translate-y-[2px] transition-transform"
              >
                Upgrade Plan
              </button>
            )}
            {isPaid && tier !== 'founding' && !isAdmin && subscription?.status !== 'cancelled' && (
              <>
                <button
                  onClick={() => navigate('/pricing')}
                  data-testid="change-plan-btn"
                  className="bg-[var(--secondary)] text-white font-bold px-6 py-3 border-2 border-[var(--border-default)]"
                >
                  Change Plan
                </button>
                <button
                  onClick={handleCancel}
                  disabled={cancelling}
                  data-testid="cancel-sub-btn"
                  className="border-2 border-[var(--border-default)] font-bold px-6 py-3 text-[var(--text-secondary)] hover:bg-[var(--surface-alt)] transition-colors disabled:opacity-50"
                >
                  {cancelling ? 'Cancelling...' : 'Cancel Subscription'}
                </button>
              </>
            )}
          </div>
        </div>

        {/* Features */}
        <div className="bg-[var(--surface)] border-2 border-[var(--border-default)] p-8 mb-6">
          <h2 className="text-xl font-black mb-4" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
            Your Features
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {[
              { label: 'Ideas', value: isPaid || isAdmin ? 'Unlimited' : '50 max', active: true },
              { label: 'AI Enhancements', value: isPaid || isAdmin ? 'Unlimited' : '20/month', active: true },
              { label: 'Templates', value: isPaid || isAdmin ? 'All 6' : 'Basic', active: true },
              { label: 'Categories & Tags', value: 'Included', active: true },
              { label: 'Search & Archive', value: 'Included', active: true },
              { label: 'Team Features', value: tier === 'business' ? 'Included' : 'Business only', active: tier === 'business' }
            ].map((f, i) => (
              <div key={i} className={`p-3 border ${f.active ? 'border-[var(--border-default)] bg-[var(--surface-alt)]' : 'border-[var(--border-subtle)] opacity-50'}`}>
                <div className="text-sm font-bold">{f.label}</div>
                <div className="text-xs text-[var(--text-secondary)]">{f.value}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Referral Section */}
        <div className="bg-[var(--surface)] border-2 border-[var(--border-default)] p-8 mb-6" data-testid="referral-section">
          <div className="flex items-center gap-2 mb-4">
            <Gift size={20} className="text-[var(--primary)]" />
            <h2 className="text-xl font-black" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              Refer & Earn
            </h2>
          </div>
          <p className="text-sm text-[var(--text-secondary)] mb-4">
            Share VAULT with friends. When they sign up, both of you benefit!
          </p>

          {referral?.code ? (
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="flex-1 bg-[var(--surface-alt)] border-2 border-[var(--border-default)] p-3 font-mono text-lg font-bold tracking-wider">
                  {referral.code}
                </div>
                <button
                  onClick={copyCode}
                  data-testid="copy-code-btn"
                  className="p-3 border-2 border-[var(--border-default)] hover:bg-[var(--surface-alt)] transition-colors"
                >
                  {copied ? <Check size={20} className="text-green-500" /> : <Copy size={20} />}
                </button>
              </div>
              <div className="flex items-center gap-4 text-sm text-[var(--text-secondary)] mb-4">
                <span>{referral.uses || 0} referrals</span>
              </div>
              <button
                onClick={copyReferralLink}
                data-testid="copy-referral-link-btn"
                className="flex items-center gap-2 text-sm font-medium text-[var(--secondary)] hover:underline"
              >
                <Link size={14} /> Copy referral link
              </button>
            </div>
          ) : (
            <button
              onClick={handleGenerateReferral}
              disabled={generatingCode}
              data-testid="generate-referral-btn"
              className="bg-[var(--obsidian)] text-white font-bold px-6 py-3 border-2 border-[var(--border-default)] hover:translate-x-[2px] hover:translate-y-[2px] transition-transform disabled:opacity-50 flex items-center gap-2"
            >
              {generatingCode ? <Loader size={16} className="animate-spin" /> : <Gift size={16} />}
              Generate Referral Code
            </button>
          )}
        </div>

        {/* Payment History */}
        {subscription?.transactions?.length > 0 && (
          <div className="bg-[var(--surface)] border-2 border-[var(--border-default)] p-8">
            <h2 className="text-xl font-black mb-4" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
              Payment History
            </h2>
            <div className="space-y-3">
              {subscription.transactions.map((t, i) => (
                <div key={i} className="flex items-center justify-between p-3 border border-[var(--border-subtle)]">
                  <div>
                    <div className="text-sm font-bold">{t.package_name}</div>
                    <div className="text-xs text-[var(--text-secondary)]">
                      {t.created_at ? new Date(t.created_at).toLocaleDateString() : 'N/A'}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-bold">${t.amount?.toFixed(2)}</div>
                    <div className={`text-xs font-medium ${t.payment_status === 'paid' ? 'text-green-600' : 'text-yellow-600'}`}>
                      {t.payment_status}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SubscriptionPage;
